import { Box, Button, Flex, Grid, SimpleGrid, Skeleton, Text, VStack } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { RiEditBoxLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { openAddPaymentSubject } from "../../../modules/admin-modules/models/Actions";
import { brandPalette } from "../../../constants/brandPalette";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  surface: brandPalette.cream,
  canvas: brandPalette.warmBeige,
  muted: "rgba(69, 11, 17, 0.65)",
};

const PaymentSubjectTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.videos.isLoading);

  const filtered = useMemo(() => {
    const list = Array.isArray(paymentSubjects) ? paymentSubjects : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => String(row?.name || "").toLowerCase().includes(q));
  }, [paymentSubjects, searchTerm]);

  if (isLoading) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height="140px" borderRadius="16px" startColor="gray.200" endColor="gray.300" />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!filtered.length) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <Box
          border="1px dashed"
          borderColor={C.border}
          borderRadius="16px"
          bg="rgba(255,255,255,0.65)"
          py={16}
          textAlign="center"
        >
          <Text fontWeight="800" color={C.primary} fontSize="lg">
            {searchTerm ? "No payment subjects match your search" : "No payment subjects yet"}
          </Text>
          <Text mt={2} color={C.muted} fontSize="sm" maxW="md" mx="auto">
            Add a subject to label fees and set default amounts for checkout.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
        {filtered.map((row) => (
          <VStack
            key={row.id}
            align="stretch"
            spacing={4}
            border="1px solid"
            borderColor={C.border}
            borderRadius="16px"
            p={5}
            bg="rgba(255,255,255,0.92)"
            boxShadow="0 4px 18px rgba(69, 11, 17, 0.06)"
            transition="box-shadow 0.2s, border-color 0.2s"
            _hover={{
              boxShadow: "0 10px 28px rgba(69, 11, 17, 0.1)",
              borderColor: "rgba(91, 13, 22, 0.35)",
            }}
          >
            <Flex justify="space-between" align="flex-start" gap={3}>
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  Payment label
                </Text>
                <Text fontSize="lg" fontWeight="800" color={C.secondary} mt={1} noOfLines={2}>
                  {row?.name}
                </Text>
              </Box>
              <Button
                size="sm"
                leftIcon={<RiEditBoxLine />}
                variant="outline"
                borderColor={C.border}
                color={C.primary}
                borderRadius="full"
                fontWeight="700"
                flexShrink={0}
                _hover={{ bg: C.canvas, borderColor: C.primary }}
                onClick={() => {
                  setSelectedData(row);
                  setAdd(false);
                  dispatch(openAddPaymentSubject());
                }}
              >
                Edit
              </Button>
            </Flex>

            <Box pt={1} borderTop="1px solid" borderColor="rgba(216, 206, 195, 0.6)">
              <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                Amount
              </Text>
              <Text fontSize="sm" fontWeight="700" color={C.primary} mt={1}>
                {row?.value != null ? String(row.value) : "—"}
              </Text>
            </Box>
          </VStack>
        ))}
      </Grid>
    </Box>
  );
};

export default PaymentSubjectTable;
